// src/context/AuthContext.tsx
// Production-ready AuthContext & AuthProvider implementation matching BEST_PRACTICES.md Section 6.
import React, { createContext, useContext, useEffect, useState } from 'react'
import { authService, type UserProfile, type ProfileUpdateRequest } from '../services/authService'
import { store } from '@/app/store'
import { setUser as setReduxUser, clearUser as clearReduxUser } from '@/features/auth/auth.slice'

export interface AuthContextType {
  user: UserProfile | null
  loading: boolean
  login: (email: string, pass: string) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (data: ProfileUpdateRequest) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Session restoration on mount
    authService
      .initialize()
      .then((profile) => {
        setUser(profile)
        if (profile) {
          store.dispatch(setReduxUser(profile as any))
        }
      })
      .finally(() => setLoading(false))

    // Listen for session expiry event from Axios interceptor (BEST_PRACTICES.md Section 6)
    const handleExpired = () => {
      setUser(null)
      store.dispatch(clearReduxUser())
      const path = window.location.pathname
      const isPublicRoute =
        path === '/login' ||
        path === '/login/' ||
        path === '/register' ||
        path === '/register/' ||
        path.startsWith('/register') ||
        path === '/forgot-password' ||
        path === '/forgot-password/' ||
        path.startsWith('/forgot-password') ||
        path === '/unauthorized' ||
        path.startsWith('/verify-certificate') ||
        path.startsWith('/certificates/verify')

      if (!isPublicRoute) {
        window.location.href = '/login'
      }
    }

    window.addEventListener('auth:session-expired', handleExpired)
    return () => window.removeEventListener('auth:session-expired', handleExpired)
  }, [])

  const login = async (email: string, pass: string) => {
    const profile = await authService.login(email, pass)
    setUser(profile)
    if (profile) {
      store.dispatch(setReduxUser(profile as any))
    }
  }

  const logout = async () => {
    await authService.logout()
    setUser(null)
    store.dispatch(clearReduxUser())
  }

  const updateProfile = async (data: ProfileUpdateRequest) => {
    const updated = await authService.updateProfile(data)
    setUser(updated)
    if (updated) {
      store.dispatch(setReduxUser(updated as any))
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

